import { useState } from 'react';
import { updateStudioVideo } from '@/api/studio-video';
import { useCurrentApp } from '@/stores/userDataStore';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import { Box, Form, FormLayout, TextField } from '@shopify/polaris';
import { useMutation } from '@tanstack/react-query';
import { type StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';
import { useAllAiCreationsCache } from '@/features/my-creations-library/hooks/useAllAiCreations';
import { useStudioVideoCache } from '@/features/studio-videos/hooks/useStudioVideos';
import { useToast } from '@/hooks/useToast';
import BasicBanner from '@/components/BasicBanner';

type RenameStudioVideoModalProps = {
  video: StudioVideo;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const RenameStudioVideoModal = ({ video, isOpen, setIsOpen }: RenameStudioVideoModalProps) => {
  const [newName, setName] = useState(video.name || '');
  const [error, setError] = useState<string>('');
  const { appKey } = useCurrentApp();
  const { updateStudioVideoItemCache } = useStudioVideoCache();
  const { notify, notifyError } = useToast();
  const { updateStudioVideosCache } = useAllAiCreationsCache({ appKey });
  const renameMutation = useMutation({
    mutationFn: (name: string) => updateStudioVideo({ appKey, id: video.id, name }),
    onSuccess: () => {
      updateStudioVideoItemCache({ ...video, name: newName });
      updateStudioVideosCache([{ ...video, name: newName }]);
      setIsOpen(false);
      notify('Video updated');
    },
    onError: () => {
      setError('Failed to update video');
    },
  });

  const handleClose = () => {
    setName(video.name || '');
    setIsOpen(false);
  };

  const handleSave = (event: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => {
    event.preventDefault();
    if (!newName || renameMutation.isPending || newName === video.name) return;
    renameMutation.mutate(newName);
  };

  return (
    <Modal open={isOpen} onHide={handleClose}>
      <TitleBar title="Rename Creation">
        <button
          variant="primary"
          onClick={handleSave}
          disabled={newName.length === 0 || renameMutation.isPending || newName === video.name}
        >
          Save
        </button>
        <button onClick={handleClose} disabled={renameMutation.isPending}>
          Cancel
        </button>
      </TitleBar>
      <Box padding="400">
        <BasicBanner
          tone="critical"
          title="Error"
          hidden={!error}
          boxComponentProps={{ paddingBlockEnd: '200' }}
          onDismiss={() => setError('')}
        >
          {error}
        </BasicBanner>
        <Form onSubmit={handleSave}>
          <FormLayout>
            <TextField
              loading={renameMutation.isPending}
              disabled={renameMutation.isPending}
              label="Name:"
              value={newName}
              onChange={setName}
              autoComplete="off"
              requiredIndicator
              minLength={1}
            />
          </FormLayout>
        </Form>
      </Box>
    </Modal>
  );
};
